/**
 * useTheme — React binding for the theme module.
 *
 * Reads mode via useSyncExternalStore (concurrent-safe) and exposes setters
 * that route through the module's single applyTheme entry point.
 */
import { useSyncExternalStore, useCallback } from "react";
import {
  applyTheme,
  getMode,
  resolveIsDark,
  subscribe,
  type ThemeMode,
} from "@/shared/lib/theme";

function getServerSnapshot(): ThemeMode {
  return "system";
}

export interface UseThemeReturn {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export function useTheme(): UseThemeReturn {
  const mode = useSyncExternalStore(subscribe, getMode, getServerSnapshot);
  const isDark = resolveIsDark(mode);

  const setMode = useCallback((next: ThemeMode) => {
    applyTheme(next);
  }, []);

  const toggle = useCallback(() => {
    applyTheme(resolveIsDark() ? "light" : "dark");
  }, []);

  return { mode, isDark, setMode, toggle };
}
